import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { categoryList } from '@/mocks/categories';
import { useFavoriteCount } from '@/hooks/useFavorites';
import { films } from '@/mocks/films';

const navItems = [
  { label: '首页', to: '/' },
  ...categoryList.map((item) => ({ label: item.label, to: item.path })),
  { label: '关于我们', to: '/about' },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const favoriteCount = useFavoriteCount();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  const solid = scrolled || !isHome || menuOpen;

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const word = keyword.trim();
    if (!word) return;
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(word)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-colors duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span
            className={`w-9 h-9 rounded-md flex items-center justify-center ${
              solid ? 'bg-primary-500 text-background-50' : 'bg-background-50/95 text-primary-600'
            }`}
          >
            <i className="ri-play-fill text-lg leading-none" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`text-base md:text-lg font-bold tracking-tight ${
                solid ? 'text-foreground-950' : 'text-background-50'
              }`}
            >
              91在线视频
            </span>
            <span
              className={`hidden sm:block text-[10px] tracking-[0.22em] mt-1 ${
                solid ? 'text-foreground-500' : 'text-background-200'
              }`}
            >
              HD STREAMING
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${
                  isActive
                    ? solid
                      ? 'text-primary-600 font-semibold bg-primary-50'
                      : 'text-background-50 font-semibold bg-background-50/15'
                    : solid
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                      : 'text-background-100/90 hover:text-background-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden md:block relative">
            {searchOpen ? (
              <form onSubmit={submitSearch} className="flex items-center">
                <input
                  autoFocus
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  onBlur={() => {
                    if (!keyword.trim()) setSearchOpen(false);
                  }}
                  placeholder="搜索影视作品"
                  className="w-52 h-9 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-300 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="打开搜索"
                className={`w-9 h-9 rounded-md flex items-center justify-center cursor-pointer transition-colors ${
                  solid
                    ? 'text-foreground-700 hover:bg-background-100'
                    : 'text-background-50 hover:bg-background-50/15'
                }`}
              >
                <i className="ri-search-line text-base" />
              </button>
            )}
          </div>

          <Link
            to="/search"
            className={`md:hidden w-9 h-9 rounded-md flex items-center justify-center ${
              solid ? 'text-foreground-700' : 'text-background-50'
            }`}
            aria-label="搜索"
          >
            <i className="ri-search-line text-base" />
          </Link>

          <Link
            to="/search?q="
            className={`hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-md text-xs whitespace-nowrap ${
              solid
                ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
            }`}
            title="我的收藏（本地保存）"
          >
            <i className="ri-heart-3-line text-sm" />
            <span>收藏 {favoriteCount}</span>
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className={`lg:hidden w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${
              solid ? 'text-foreground-950 hover:bg-background-100' : 'text-background-50'
            }`}
            aria-label="菜单"
            aria-expanded={menuOpen}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-up">
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="px-4 py-3 rounded-md bg-background-100 text-sm text-foreground-800 flex items-center justify-between"
              >
                <span className="whitespace-nowrap">{item.label}</span>
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
          </div>
          <form onSubmit={submitSearch} className="mt-3 flex items-center gap-2">
            <div className="relative flex-1">
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视作品"
                className="w-full h-11 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-300 text-foreground-950 focus:outline-none focus:border-primary-400"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            </div>
            <button
              type="submit"
              className="h-11 px-4 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <p className="mt-3 text-xs text-foreground-500">
            站点共收录 {films.length} 部影视作品 · 演示数据
          </p>
        </div>
      )}
    </header>
  );
}